import mixpanel from 'mixpanel-browser';

interface Event {
  name: string;
  extra?: (attrs: any) => any;
}

const events: Record<string, Event> = {
  signup_submit: {
    name: 'Signup Submit',
    extra: (attrs: any) => ({
      'User Email': attrs.email,
      'User Name': `${attrs.first_name} ${attrs.last_name}`,
    }),
  },

  pro_signup: {
    name: 'Pro Account Created',
    extra: (attrs: any) => ({
      'User ID': attrs.id,
      'User Email': attrs.email,
      'User Name': `${attrs.first_name} ${attrs.last_name}`,
    }),
  },

  client_accepted_invitation: {
    name: 'Client Accepted Invitation',
    extra: (attrs: any) => ({
      'User ID': attrs.id,
      'User Email': attrs.email,
      'User Name': `${attrs.first_name} ${attrs.last_name}`,
    }),
  },

  confirm_email: {
    name: 'Email Confirmed',
  },

  pro_login: {
    name: 'Pro Login',
    extra: () => ({
      'Login Time': new Date(),
    }),
  },

  client_login: {
    name: 'Client Login',
    extra: () => ({
      'Login Time': new Date(),
    }),
  },

  pro_logout: {
    name: 'Pro Logout',
    extra: () => ({
      'Logout Time': new Date(),
    }),
  },

  client_logout: {
    name: 'Client Logout',
    extra: () => ({
      'Logout Time': new Date(),
    }),
  },

  client_added: {
    name: 'Client Added',
    extra: (attrs: any) => ({
      'Client Name': attrs.full_name,
    }),
  },

  client_invited: {
    name: 'Client Invited',
    extra: (attrs: any) => ({
      'Client Name': attrs.full_name,
      'Client Email': attrs.email,
    }),
  },

  client_group_created: {
    name: 'Client Group Created',
    extra: (attrs: any) => ({
      'Client Group ID': attrs.id,
      'Client Group Name': attrs.name,
    }),
  },

  client_group_member_added: {
    name: 'Client Group Member Added',
    extra: (attrs: any) => ({
      'Group ID': attrs.groupId,
      'Group Name': attrs.groupName,
      'Client ID': attrs.clientId,
      'Client Name': attrs.clientName,
    }),
  },

  client_group_member_removed: {
    name: 'Client Group Member Removed',
    extra: (attrs: any) => ({
      'Group ID': attrs.groupId,
      'Group Name': attrs.groupName,
      'Client ID': attrs.clientId,
      'Client Name': attrs.clientName,
    }),
  },

  pro_created_exercise: {
    name: 'Pro Created Exercise',
    extra: (attrs: any) => ({
      'Exercise ID': attrs.id,
      'Exercise Name': attrs.name,
    }),
  },

  pro_created_workout: {
    name: 'Pro Created Workout',
    extra: (attrs: any) => ({
      'Workout ID': attrs.id,
      'Workout Name': attrs.name,
    }),
  },

  pro_created_program: {
    name: 'Pro Created Program',
    extra: (attrs: any) => ({
      'Program ID': attrs.id,
      'Program Name': attrs.name,
    }),
  },

  pro_created_product: {
    name: 'Pro Created Product',
    extra: (attrs: any) => ({
      'Product ID': attrs.id,
      'Product Name': attrs.name,
    }),
  },

  pro_created_product_plan: {
    name: 'Pro Created Product Plan',
    extra: (attrs: any) => ({
      'Product Plan ID': attrs.id,
      'Product Plan Name': attrs.name,
    }),
  },

  pro_assigned_exercise: {
    name: 'Pro Assigned Exercise',
    extra: (attrs: any) => ({
      'Exercise ID': attrs.id,
      'Exercise Name': attrs.name,
      'Person ID': attrs.personId,
    }),
  },

  pro_assigned_workout: {
    name: 'Pro Assigned Workout Template',
    extra: (attrs: any) => ({
      'Workout ID': attrs.id,
      'Workout Name': attrs.name,
      'Person ID': attrs.personId,
    }),
  },

  pro_assigned_program: {
    name: 'Pro Assigned Program Template',
    extra: (attrs: any) => ({
      'Program ID': attrs.id,
      'Program Name': attrs.name,
      'Person ID': attrs.personId,
    }),
  },

  pro_unassigned_exercise: {
    name: 'Pro Unassigned Exercise',
    extra: (attrs: any) => ({
      'Exercise ID': attrs.id,
      'Exercise Name': attrs.name,
      'Person ID': attrs.personId,
    }),
  },

  pro_unassigned_workout: {
    name: 'Pro Unassigned Workout Template',
    extra: (attrs: any) => ({
      'Workout ID': attrs.id,
      'Workout Name': attrs.name,
      'Person ID': attrs.personId,
    }),
  },

  pro_unassigned_program: {
    name: 'Pro Unassigned Program Template',
    extra: (attrs: any) => ({
      'Program ID': attrs.id,
      'Program Name': attrs.name,
      'Person ID': attrs.personId,
    }),
  },

  pro_started_workout: {
    name: 'Pro Started Workout',
    extra: (attrs: any) => ({
      'Person ID': attrs.person_id,
      'Person Name': attrs.person_name,
      'Workout ID': attrs.personal_workout_id,
      'Workout Name': attrs.workout_name,
      'Workout Begins At': attrs.begins_at,
      'Workout Ends At': attrs.ends_at,
    }),
  },

  client_started_workout: {
    name: 'Client Started Workout',
    extra: (attrs: any) => ({
      'Person ID': attrs.person_id,
      'Person Name': attrs.person_name,
      'Workout ID': attrs.personal_workout_id,
      'Workout Name': attrs.workout_name,
      'Workout Begins At': attrs.begins_at,
      'Workout Ends At': attrs.ends_at,
    }),
  },

  pro_scheduled_workout: {
    name: 'Pro Scheduled Workout',
    extra: (attrs: any) => ({
      'Person ID': attrs.person_id,
      'Person Name': attrs.person_name,
      'Workout ID': attrs.personal_workout_id,
      'Workout Name': attrs.workout_name,
      'Workout Begins At': attrs.begins_at,
      'Workout Ends At': attrs.ends_at,
    }),
  },

  client_scheduled_workout: {
    name: 'Client Scheduled Workout',
    extra: (attrs: any) => ({
      'Person ID': attrs.person_id,
      'Person Name': attrs.person_name,
      'Workout ID': attrs.personal_workout_id,
      'Workout Name': attrs.workout_name,
      'Workout Begins At': attrs.begins_at,
      'Workout Ends At': attrs.ends_at,
    }),
  },

  pro_completed_workout: {
    name: 'Pro Completed Workout',
    extra: (attrs: any) => ({
      'Person ID': attrs.person_id,
      'Person Name': attrs.person_name,
      'Workout ID': attrs.personal_workout_id,
      'Workout Name': attrs.workout_name,
      'Workout Begins At': attrs.begins_at,
      'Workout Ends At': attrs.ends_at,
    }),
  },

  client_completed_workout: {
    name: 'Client Completed Workout',
    extra: (attrs: any) => ({
      'Person ID': attrs.person_id,
      'Person Name': attrs.person_name,
      'Workout ID': attrs.personal_workout_id,
      'Workout Name': attrs.workout_name,
      'Workout Begins At': attrs.begins_at,
      'Workout Ends At': attrs.ends_at,
    }),
  },
};

interface Action {
  type: string;
  attrs?: any;
  eventKey?: string;
}

type NextFunction = (action: Action) => void;
const processEvent = (event: Event, attrs: any) => {
  const { name, extra } = event;
  const props = extra ? extra(attrs) : {};
  return { name, props };
};

const analyticMiddleware = () => (next: NextFunction) => (action: Action) => {
  const { attrs, eventKey } = action;

  if (action.type !== 'GC_ANALYTICS' || !eventKey || !events[eventKey]) {
    return next(action);
  }

  try {
    const event = events[eventKey];
    const { name, props } = processEvent(event, attrs);
    mixpanel.track(name, props);
  } catch (error) {
    console.error('Analytics middleware error:', error);
  }

  return next(action);
};

export default analyticMiddleware;
